import React, { useState, useEffect, useMemo } from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import api from "../../services/api";
import "../../components/admin/admin.css";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";

import {  FaChartLine, FaClock, FaCalendarAlt, FaTimesCircle } from "react-icons/fa";

const monthNames = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];



const AttendanceChart = () => {
  const [attendanceRecords, setAttendanceRecords] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState("");

  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());

  const getToken = () => localStorage.getItem("token");

  const getUser = () => {
    try {
      return JSON.parse(localStorage.getItem("user") || "{}");
    } catch {
      return {};
    }
  };

  const user = getUser();

  // ==================== Fetch Attendance ====================
  useEffect(() => {
    const fetchAttendance = async () => {
      try {
        setIsLoading(true);
        setErrorMsg("");
        const token = getToken();

        const firstDay = `${selectedYear}-${String(selectedMonth).padStart(2, "0")}-01`;
        const totalDays = new Date(selectedYear, selectedMonth, 0).getDate();
        const lastDay = `${selectedYear}-${String(selectedMonth).padStart(2, "0")}-${String(totalDays).padStart(2, "0")}`;

        const response = await api.get("/attendance/get_history", {
          params: {
            start_date: firstDay,
            end_date: lastDay,
          },
          headers: { Authorization: `Bearer ${token}` },
        });

        if (response.status === 200) {
          setAttendanceRecords(response.data.attendances || response.data.records || []);
        }
      } catch (error) {
        setErrorMsg(
          error.response?.data?.message || "Failed to fetch attendance data"
        );
      } finally {
        setIsLoading(false);
      }
    };

    fetchAttendance();
  }, [selectedMonth, selectedYear]);

  // ==================== Build chart data ====================
  const chartData = useMemo(() => {
    const totalDays = new Date(selectedYear, selectedMonth, 0).getDate();
    const today = new Date();
    const isCurrentMonth =
      selectedMonth === today.getMonth() + 1 &&
      selectedYear === today.getFullYear();
    const lastDay = isCurrentMonth ? today.getDate() : totalDays;

    // Map: dateStr -> worked hours (float)
    const hoursByDate = {};

    attendanceRecords.forEach((rec) => {
      if (!rec.check_in || !rec.check_out) return;

      const dateStr = new Date(rec.check_in).toISOString().split("T")[0];

      const checkIn = new Date(rec.check_in).getTime();
      const checkOut = new Date(rec.check_out).getTime();
      const grossMinutes = Math.floor((checkOut - checkIn) / 60000);
      const pauseMinutes = rec.total_pause_minutes || 0;
      const workedMinutes = Math.max(0, grossMinutes - pauseMinutes);

      hoursByDate[dateStr] = (hoursByDate[dateStr] || 0) + workedMinutes / 60;
    });

    // Build array for each day
    const data = [];
    for (let d = 1; d <= lastDay; d++) {
      const dateObj = new Date(selectedYear, selectedMonth - 1, d);
      const dayOfWeek = dateObj.getDay(); // 0 = Sunday
      const dateStr = `${selectedYear}-${String(selectedMonth).padStart(2, "0")}-${String(d).padStart(2, "0")}`;

      data.push({
        day: d,
        label: String(d),
        fullDate: dateObj.toLocaleDateString("en-US", {
          weekday: "short",
          month: "short",
          day: "numeric",
        }),
        isSunday: dayOfWeek === 0,
        Hours: Math.round((hoursByDate[dateStr] || 0) * 10) / 10,
      });
    }

    return data;
  }, [attendanceRecords, selectedMonth, selectedYear]);

  // ==================== Summary stats ====================
  const summary = useMemo(() => {
    const workingDays = chartData.filter((d) => !d.isSunday && d.Hours > 0);
    const totalHours = chartData.reduce((sum, d) => sum + d.Hours, 0);
    const avgHours =
      workingDays.length > 0 ? totalHours / workingDays.length : 0;
    const maxHours =
      chartData.length > 0 ? Math.max(...chartData.map((d) => d.Hours)) : 0;

    return {
      totalHours: Math.round(totalHours * 10) / 10,
      avgHours: Math.round(avgHours * 10) / 10,
      maxHours: Math.round(maxHours * 10) / 10,
      daysWorked: workingDays.length,
    };
  }, [chartData]);

  // ==================== Custom tooltip ====================
  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div
          style={{
            background: "white",
            padding: "12px 16px",
            borderRadius: "10px",
            boxShadow: "0 6px 20px rgba(0,0,0,0.12)",
            border: "1px solid #e2e8f0",
            fontSize: "13px",
            minWidth: "150px",
          }}
        >
          <div
            style={{
              fontWeight: "700",
              color: "#1e293b",
              marginBottom: "6px",
              paddingBottom: "6px",
              borderBottom: "1px solid #f1f5f9",
            }}
          >
            {data.fullDate}
          </div>
          <div
            style={{
              color: data.Hours > 0 ? "#667eea" : "#94a3b8",
              fontWeight: "600",
              display: "flex",
              justifyContent: "space-between",
            }}
          >
            <span><FaClock style={{ marginRight: "6px" }} />Hours</span>
            <span>{data.Hours}h</span>
          </div>
          {data.isSunday && (
            <div
              style={{
                color: "#94a3b8",
                fontSize: "11px",
                marginTop: "4px",
                fontStyle: "italic",
              }}
            >
              Sunday (Off day)
            </div>
          )}
        </div>
      );
    }
    return null;
  };

  // ==================== Custom dot ====================
  const CustomDot = (props) => {
    const { cx, cy, payload } = props;
    if (payload.Hours === 0) return null;
    return (
      <circle
        cx={cx}
        cy={cy}
        r={4}
        fill="white"
        stroke="#667eea"
        strokeWidth={3}
      />
    );
  };

  const availableYears = [
    new Date().getFullYear(),
    new Date().getFullYear() - 1,
    new Date().getFullYear() - 2,
  ];

  return (
    <div className="admin-container">
      {/* Header */}
      <div className="header-banner mb-4">
        <div className="header-content">
          <div>
            <h1 className="header-title" style={{ display: "flex", alignItems: "center", gap: "8px" }}><FaChartLine /> My Attendance Chart</h1>
            <p className="header-subtitle">
              {user?.employeeName || user?.name || "Employee"} - Daily working hours overview
            </p>
          </div>
          <div style={{ display: "flex", gap: "10px", flexWrap: "nowrap" }}>
            <select
              className="form-input filter-select"
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
              style={{
                minWidth: "150px",
                padding: "9px 12px",
                borderRadius: "10px",
                border: "1px solid #e2e8f0",
                background: "#ffffff",
                fontWeight: "500",
                fontSize: "13px",
                color: "#1e293b",
                cursor: "pointer",
                outline: "none",
              }}
            >
              {monthNames.map((m, i) => (
                <option key={i} value={i + 1}>
                  {m}
                </option>
              ))}
            </select>
            <select
              className="form-input filter-select"
              value={selectedYear}
              onChange={(e) => setSelectedYear(parseInt(e.target.value))}
              style={{
                minWidth: "110px",
                padding: "9px 12px",
                borderRadius: "10px",
                border: "1px solid #e2e8f0",
                background: "#ffffff",
                fontWeight: "500",
                fontSize: "13px",
                color: "#1e293b",
                cursor: "pointer",
                outline: "none",
              }}
            >
              {availableYears.map((y) => (
                <option key={y} value={y}>
                  {y}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {errorMsg && (
        <div className="error-message" style={{ marginBottom: "20px" }}>
          <FaTimesCircle style={{ marginRight: "6px" }} /> {errorMsg}
        </div>
      )}

      {isLoading ? (
        <div style={{ textAlign: "center", padding: "80px" }}>
          <div className="loading-spinner"></div>
          <p style={{ marginTop: "15px", color: "#888" }}>
            Loading attendance data...
          </p>
        </div>
      ) : (
        <>
          {/* Summary Cards */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
              gap: "14px",
              marginBottom: "20px",
            }}
          >
            <div
              style={{
                background: "linear-gradient(135deg, #eef2ff, #e0e7ff)",
                border: "1px solid #c7d2fe",
                borderRadius: "14px",
                padding: "16px 18px",
                display: "flex",
                alignItems: "center",
                gap: "12px",
              }}
            >
              <span style={{ fontSize: "26px" }}><FaClock /></span>
              <div>
                <div
                  style={{
                    fontSize: "11px",
                    color: "#4338ca",
                    fontWeight: "700",
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                  }}
                >
                  Total Hours
                </div>
                <div
                  style={{
                    fontSize: "22px",
                    fontWeight: "800",
                    color: "#3730a3",
                    lineHeight: "1.1",
                  }}
                >
                  {summary.totalHours}h
                </div>
              </div>
            </div>

            <div
              style={{
                background: "linear-gradient(135deg, #ecfdf5, #d1fae5)",
                border: "1px solid #86efac",
                borderRadius: "14px",
                padding: "16px 18px",
                display: "flex",
                alignItems: "center",
                gap: "12px",
              }}
            >
              <span style={{ fontSize: "26px" }}><FaCalendarAlt /></span>
              <div>
                <div
                  style={{
                    fontSize: "11px",
                    color: "#065f46",
                    fontWeight: "700",
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                  }}
                >
                  Days Worked
                </div>
                <div
                  style={{
                    fontSize: "22px",
                    fontWeight: "800",
                    color: "#047857",
                    lineHeight: "1.1",
                  }}
                >
                  {summary.daysWorked}
                </div>
              </div>
            </div>

            <div
              style={{
                background: "linear-gradient(135deg, #fef9c3, #fef08a)",
                border: "1px solid #fde047",
                borderRadius: "14px",
                padding: "16px 18px",
                display: "flex",
                alignItems: "center",
                gap: "12px",
              }}
            >
              <span style={{ fontSize: "26px" }}><FaChartLine /></span>
              <div>
                <div
                  style={{
                    fontSize: "11px",
                    color: "#854d0e",
                    fontWeight: "700",
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                  }}
                >
                  Avg / Day
                </div>
                <div
                  style={{
                    fontSize: "22px",
                    fontWeight: "800",
                    color: "#a16207",
                    lineHeight: "1.1",
                  }}
                >
                  {summary.avgHours}h
                </div>
              </div>
            </div>

            <div
              style={{
                background: "linear-gradient(135deg, #fce7f3, #fbcfe8)",
                border: "1px solid #f9a8d4",
                borderRadius: "14px",
                padding: "16px 18px",
                display: "flex",
                alignItems: "center",
                gap: "12px",
              }}
            >
              <span style={{ fontSize: "26px" }}>🔥</span>
              <div>
                <div
                  style={{
                    fontSize: "11px",
                    color: "#9f1239",
                    fontWeight: "700",
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                  }}
                >
                  Best Day
                </div>
                <div
                  style={{
                    fontSize: "22px",
                    fontWeight: "800",
                    color: "#be185d",
                    lineHeight: "1.1",
                  }}
                >
                  {summary.maxHours}h
                </div>
              </div>
            </div>
          </div>

          {/* Chart Card */}
          <div className="students-card" style={{ marginBottom: "20px" }}>
            <div className="card-header-custom">
              <div>
                <h3 className="card-title">
                  {monthNames[selectedMonth - 1]} {selectedYear} - Daily Hours
                </h3>
                <p className="card-subtitle">
                  Working hours trend for the month
                </p>
              </div>
            </div>
            <div className="card-body-custom">
              <div style={{ width: "100%", height: 420 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart
                    data={chartData}
                    margin={{ top: 20, right: 30, left: 0, bottom: 10 }}
                  >
                    <CartesianGrid
                      strokeDasharray="3 3"
                      stroke="#e2e8f0"
                      vertical={true}
                      horizontal={true}
                    />
                    <XAxis
                      dataKey="label"
                      tick={{ fontSize: 12, fill: "#64748b" }}
                      axisLine={{ stroke: "#cbd5e1" }}
                      tickLine={false}
                      interval={0}
                    />
                    <YAxis
                      tick={{ fontSize: 12, fill: "#64748b" }}
                      axisLine={{ stroke: "#cbd5e1" }}
                      tickLine={false}
                      label={{
                        value: "Hours",
                        angle: -90,
                        position: "insideLeft",
                        style: {
                          fontSize: "12px",
                          fill: "#94a3b8",
                          fontWeight: "600",
                        },
                      }}
                    />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend
                      wrapperStyle={{
                        fontSize: "13px",
                        paddingTop: "10px",
                      }}
                      iconType="circle"
                    />
                    <Line
                      type="monotone"
                      dataKey="Hours"
                      stroke="#667eea"
                      strokeWidth={3}
                      dot={<CustomDot />}
                      activeDot={{ r: 7, fill: "#667eea" }}
                      name="Hours Worked"
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>

          {/* Legend / Info */}
          <div className="students-card">
            <div className="card-body-custom">
              <div
                style={{
                  display: "flex",
                  gap: "20px",
                  flexWrap: "wrap",
                  fontSize: "13px",
                  color: "#64748b",
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                  <span
                    style={{
                      width: "12px",
                      height: "12px",
                      borderRadius: "50%",
                      background: "#667eea",
                      display: "inline-block",
                    }}
                  ></span>
                  <span>
                    <strong style={{ color: "#1e293b" }}>Worked day</strong> - Hours shown as blue dot
                  </span>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                  <span
                    style={{
                      width: "12px",
                      height: "12px",
                      borderRadius: "50%",
                      background: "#e2e8f0",
                      display: "inline-block",
                    }}
                  ></span>
                  <span>
                    <strong style={{ color: "#1e293b" }}>No work / Sunday</strong> - No dot shown
                  </span>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default AttendanceChart;